/**
 * Générateur, Attendu 182, « Comprendre l'effet d'une translation : conservation
 * du parallélisme, des longueurs, des aires et des angles. »
 * Niveau : 4ème | Thème #9 Transformations (O5) | Espace et géométrie.
 * Source prof : « TANOED EXERCISEUR GRANDEURS ET MESURES 4EME.pdf » (Ex.4-8,
 * D2S1.Ex.11, D2S2.Ex.2/7/8, D2S3.Ex.3/5), extraction grandeurs_mesures.
 *
 * Historique (juillet 2026, état remplacé par la revue du 2026-09-23) : huit
 * cartes, construire l'image (placer_points_2d), coordonnées de l'image,
 * reconnaître une translation (QCM figure), ce que conserve une translation
 * (V/F), nature du quadrilatère MM'N'N (QCM), aire d'une frise (n × a), angle
 * conservé (somme des angles), translation réciproque (QCM). Les démonstrations
 * libres du doc (justifier, démontrer) y étaient rendues calculables ou en QCM.
 *
 * Revue 2026-09-23 (vague 4, la translation ; mode auto, décisions du
 * coordinateur, cadrage project_cadrage_182-187-188) :
 * Plafond : « Il comprend l'effet d'une translation : conservation du
 * parallélisme, des longueurs, des aires et des angles. » Exemples de réussite :
 * « Il détermine des longueurs, des aires et des mesures d'angles en utilisant
 * les propriétés de conservation de la translation. » et « Il détermine la
 * nature du quadrilatère ABCD sur la figure c, construite à l'aide de
 * translations » (attendus de fin de 4e, Eduscol). Cours du professeur :
 * « La translation conserve le parallélisme, l'alignement, les longueurs, les
 * angles, les aires », « ABM'M est un parallélogramme », la flèche (direction,
 * sens, longueur) ; le mot « vecteur » n'y figure pas.
 *   - « Construire l'image par une translation » (construire_image) et
 *     « Coordonnées de l'image » (coord_image_texte_trou) : DÉPLACÉES dans 187
 *     (construire_placer_points_2d, image_point_texte_trou_saisie), c'est la
 *     ligne « Il transforme une figure par translation » ; retirées d'ici.
 *     1245 (automatisme de 3e) les délègue désormais à 187.
 *   - « Reconnaître une translation » (transformation_qcm) : SORTIE. La flèche
 *     tracée d'un sommet à son image donnait la réponse, la clé était la même
 *     à chaque tirage, et un triangle isocèle d'axe perpendiculaire au
 *     déplacement rendait aussi juste « symétrie axiale » (0,8 % des tirages).
 *     « Quelle transformation ? » vit dans 188 et 1245.
 *   - « Translation réciproque » (reciproque_qcm) : SORTIE (la translation
 *     réciproque, l'opposé d'un vecteur, est un objectif de 3e ; on y
 *     répondait par élimination dans 100 % des tirages).
 *   - « Ce que conserve une translation » (conservation_vf) : gardée. Quatre
 *     lignes au lieu de cinq (règle du professeur), une à trois vraies tirées
 *     (avant : toujours trois vraies et deux fausses), une seule ligne par
 *     grandeur (avant : « conserve les longueurs » et « change les longueurs »
 *     ensemble dans 49,9 % des tirages, l'une décidait l'autre), l'alignement
 *     ajouté (propriété du cours) et « tous les points glissent de la même
 *     façon : même direction, même sens, même longueur ». Fausses nommées :
 *     agrandissement, glissement_pour_miroir, demi_tour_pour_translation,
 *     conservation_niee. Pur rappel : pas de fiche.
 *   - « Nature du quadrilatère » (nature_quad_qcm) : recadrée avec une figure
 *     (quadrillage, M et N, leurs images M' et N', la flèche de la translation
 *     tracée à part, MM'N'N en pointillés), sans « vecteur » (49,3 % des
 *     tirages) ni « égaux et parallèles » (la méthode soufflée, 50,7 %).
 *     Tirage sans angle droit ni côtés égaux, M, N, M' non alignés (ni
 *     rectangle ni losange à l'œil). Distracteurs parallelogramme_particulier
 *     (losange, rectangle) et trapeze ; la consigne demande la nature « la plus
 *     précise » parce qu'au sens inclusif un parallélogramme est aussi un
 *     trapèze (convention de gen_1079). Mode auto, À ARBITRER : pour que la clé
 *     ne soit pas la même à chaque tirage, le sousType « propriete » une fois
 *     sur deux, « Pourquoi le quadrilatère MM'N'N est-il un parallélogramme ? »,
 *     clé « Les flèches de M à M' et de N à N' ont la même direction, le même
 *     sens et la même longueur. » (cours), contre insuffisant (une seule paire
 *     de côtés de même longueur), losange (diagonales perpendiculaires) et
 *     rectangle (un angle droit), écrits en phrases de longueur voisine pour
 *     que la clé ne soit pas la seule longue. C'est le QCM de l'attendu.
 *   - « Aire d'une frise » (aire_frise_saisie) : recadrée avec une figure
 *     (frise de 3 à 6 motifs numérotés sur quadrillage, sommets entiers, aire
 *     du motif 6 à 20 carreaux vérifiée par airePoly, throw sinon) ; plus de
 *     « identiques » ni de « Comme la translation conserve les aires » (la
 *     règle était écrite dans 100 % des tirages, il ne restait que n × a) ;
 *     « Les motifs 2, 3… sont obtenus à partir du motif 1 par des
 *     translations. », aire du motif 1 donnée dans le texte, question « aire du
 *     motif k » ou « aire de toute la frise ». Motifs à côtés obliques
 *     (chevron de l'exemple officiel, parallélogramme, trapèze, triangle,
 *     maison) pour que la conservation serve plus que le comptage.
 *   - « Angle conservé » (angle_saisie) : figure codée (triangle ABC rectangle
 *     en A, un angle aigu marqué, son image MNP et la flèche de la
 *     translation) ; plus de phrase de conservation dans l'énoncé (« MNP est
 *     aussi rectangle en M » soufflait tout, 100 %) ; deux régimes : sousType
 *     « direct » (l'image de l'angle marqué) et « troisieme_angle » (l'image
 *     de l'autre angle aigu, 90 − c, contenu.difficulte = 'troisieme_angle',
 *     ECHELONS_DIFFICULTE) ; c ≠ 45 (sinon recopier l'angle donné, erreur
 *     angle_recopie, était juste : 1,9 %) ; notations tex('\\widehat{MNP}') au
 *     lieu des chapeaux Unicode ; les parenthèses réécrites en phrase.
 * Figures : SVG en imageUrl avec un alt qui décrit sans donner la réponse
 * (svgQuadrillage de transformations.js, figure d'angle locale). Déplacement
 * sacrifié du cours (4 carreaux à droite, 1 vers le haut) exclu de tous les
 * tirages. Pas de rappel brouillon (tout se fait de tête). Aucun solveur
 * test-side ne couvre 182 (l'ancien en-tête citait à tort
 * transformations-4e.js, qui ne sert que 1119 à 1121) ; la carte « Ce que
 * conserve une translation » est relue par le solveur de 1245 qui la délègue
 * (tests/solvers/transformations-3e.js).
 */

import {
    randInt, randomChoice, shuffle, makeQcm, makeSaisie, makeVraiFaux, tex,
} from '../../../utils';
import {
    airePoly, svgQuadrillage, imageEnonce, distPointSegment, BLEU, ORANGE, ROUGE, VERT,
} from './transformations';

const ATTENDU_ID = 182;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CONSV = 'Ce que conserve une translation';
const L_QUAD = 'Nature du quadrilatère';
const L_FRISE = 'Aire d\'une frise';
const L_ANGLE = 'Angle conservé';

const egal = (p, q) => p[0] === q[0] && p[1] === q[1];
const plus = (p, v) => [p[0] + v[0], p[1] + v[1]];
const moins = (p, q) => [p[0] - q[0], p[1] - q[1]];
// Le déplacement de l'exemple du cours (p. 3 : 4 carreaux à droite, 1 vers le haut) ne sort jamais.
const SACRIFIE = [4, 1];

// ═════════ Carte « Ce que conserve une translation » (V/F, quatre lignes) ═════════

// Une famille = une grandeur (ou un mouvement) : jamais deux lignes de la même
// famille dans un tableau, sinon l'une décide l'autre (« conserve » et
// « change » les longueurs). « Agrandit » parle des longueurs.
const LIGNES_VF = [
    { l: 'Une translation conserve les longueurs.', v: true, fam: 'longueurs' },
    { l: 'Une translation conserve les aires.', v: true, fam: 'aires' },
    { l: 'Une translation conserve les mesures d\'angles.', v: true, fam: 'angles' },
    { l: 'Une translation conserve le parallélisme de deux droites.', v: true, fam: 'parallelisme' },
    { l: 'Une translation conserve l\'alignement des points.', v: true, fam: 'alignement' },
    { l: 'Dans une translation, tous les points glissent de la même façon : même direction, même sens, même longueur.', v: true, fam: 'glissement' },
    { l: 'Une translation agrandit les figures.', v: false, fam: 'longueurs', code: 'agrandissement' },
    { l: 'Une translation change les longueurs.', v: false, fam: 'longueurs', code: 'conservation_niee' },
    { l: 'Une translation change les aires.', v: false, fam: 'aires', code: 'conservation_niee' },
    { l: 'Une translation change la mesure des angles.', v: false, fam: 'angles', code: 'conservation_niee' },
    { l: 'Une translation retourne la figure comme un miroir.', v: false, fam: 'miroir', code: 'glissement_pour_miroir' },
    { l: 'Une translation fait tourner la figure d\'un demi-tour.', v: false, fam: 'demi_tour', code: 'demi_tour_pour_translation' },
];
export const VERITES_CONSERVATION = Object.fromEntries(LIGNES_VF.map((x) => [x.l, x.v]));

const genConservation = () => {
    const nbFaux = randInt(1, 3);                             // donc une à trois vraies
    const famFaux = shuffle([...new Set(LIGNES_VF.filter((x) => !x.v).map((x) => x.fam))]).slice(0, nbFaux);
    const famVrai = shuffle([...new Set(LIGNES_VF.filter((x) => x.v).map((x) => x.fam))].filter((f) => !famFaux.includes(f))).slice(0, 4 - nbFaux);
    const pris = [
        ...famFaux.map((f) => randomChoice(LIGNES_VF.filter((x) => !x.v && x.fam === f))),
        ...famVrai.map((f) => randomChoice(LIGNES_VF.filter((x) => x.v && x.fam === f))),
    ];
    const lignes = shuffle(pris);
    // Second chemin : quatre lignes de familles distinctes, au moins une vraie et une fausse, étiquettes = vérités.
    const nbV = lignes.filter((x) => VERITES_CONSERVATION[x.l]).length;
    if (lignes.length !== 4 || new Set(lignes.map((x) => x.fam)).size !== 4 || nbV < 1 || nbV > 3) {
        throw new Error('gen_182 conservation : tableau');
    }
    const payload = makeVraiFaux(lignes.map((x) => x.l), lignes.map((x) => (VERITES_CONSERVATION[x.l] ? 0 : 1)),
        '<p><b>Vrai ou faux ?</b> Coche pour chaque affirmation sur les translations.</p>',
        opts(L_CONSV));
    payload._meta.params = { distracteurs: lignes.filter((x) => x.code).map(({ code }) => ({ code })) };
    return payload;
};

// ═════════ Carte « Nature du quadrilatère » (QCM, figure) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'jamais « vecteur », jamais « égaux et parallèles » ni aucune propriété soufflée dans l\'énoncé : la translation est schématisée par une flèche tracée à part, les propriétés se lisent sur la figure',
        'le déplacement sacrifié du cours (4 carreaux à droite, 1 vers le haut) ne sort jamais',
    ],
    nature_quad_qcm: {
        support: 'quadrillage : M et N (bleus), leurs images M\' et N\' (orange) par la translation schématisée par une flèche verte tracée à part, le quadrilatère MM\'N\'N en pointillés ; tirage sans angle droit (|cos| ≥ 0,25) ni côtés égaux (rapport des côtés ≥ 1,3), aire au moins 8 carreaux',
        nature: {
            cle: 'Un parallélogramme.',
            familles: {
                parallelogramme_particulier: 'Un losange. / Un rectangle. : prendre le cas particulier pour le cas général ; faux ici par construction (côtés voisins de longueurs nettement différentes, aucun angle droit)',
                trapeze: 'Un trapèze. : ne voir qu\'une paire de côtés parallèles ; la consigne demande la nature la plus précise, parce qu\'au sens inclusif un parallélogramme est aussi un trapèze (convention de gen_1079)',
            },
        },
        propriete: {
            cle: 'Les flèches de M à M\' et de N à N\' ont la même direction, le même sens et la même longueur. (cours : « les deux flèches ont la même direction, le même sens et la même longueur »)',
            familles: {
                insuffisant: 'une seule paire de côtés opposés de même longueur, vraie mais qui ne suffit pas (un trapèze isocèle l\'a aussi)',
                losange: 'des diagonales perpendiculaires : fausses ici et propriété du losange',
                rectangle: 'un angle droit : faux ici et propriété du rectangle',
            },
        },
        garantie: 'la clé est vraie par construction (M\' − M = N\' − N) et c\'est la seule : le tirage exclut losange et rectangle, et « la plus précise » exclut le trapèze ; les trois raisons distractrices sont fausses ou insuffisantes pour tout tirage',
    },
};

const CHOIX_NATURE = { cle: 'Un parallélogramme.', losange: 'Un losange.', rectangle: 'Un rectangle.', trapeze: 'Un trapèze.' };
const CHOIX_PROPRIETE = {
    cle: 'Les flèches de M à M\' et de N à N\' ont la même direction, le même sens et la même longueur.',
    insuffisant: 'Les côtés [MN] et [M\'N\'] ont la même longueur, puisque la translation conserve les longueurs.',
    losange: 'Ses diagonales [MN\'] et [M\'N] sont perpendiculaires.',
    rectangle: 'Ses côtés [MM\'] et [MN] sont perpendiculaires : il a un angle droit en M.',
};

const QX = 14;
const QY = 10;
const dansQ = (p) => p[0] >= 1 && p[0] <= QX - 1 && p[1] >= 1 && p[1] <= QY - 1;
const cross = (u, w) => u[0] * w[1] - u[1] * w[0];

/** Distance entre deux segments (nulle s'ils se coupent). */
const distSegments = (a, b, c, d) => {
    if (Math.sign(cross(moins(b, a), moins(c, a))) * Math.sign(cross(moins(b, a), moins(d, a))) < 0
        && Math.sign(cross(moins(d, c), moins(a, c))) * Math.sign(cross(moins(d, c), moins(b, c))) < 0) return 0;
    return Math.min(distPointSegment(a, c, d), distPointSegment(b, c, d), distPointSegment(c, a, b), distPointSegment(d, a, b));
};

/** Propriétés du quadrilatère M, M', N', N recalculées sur ses quatre sommets (second chemin, sans le vecteur). */
const natureRecalculee = (M, Mp, Np, N) => {
    const cotes = [moins(Mp, M), moins(Np, Mp), moins(N, Np), moins(M, N)];
    const parallelogramme = egal(cotes[0], [-cotes[2][0], -cotes[2][1]]) && egal(cotes[1], [-cotes[3][0], -cotes[3][1]]);
    const l2 = cotes.map((c) => c[0] ** 2 + c[1] ** 2);
    const angleDroit = cotes.some((c, i) => { const s = cotes[(i + 1) % 4]; return c[0] * s[0] + c[1] * s[1] === 0; });
    return { parallelogramme, losange: new Set(l2).size === 1, rectangle: angleDroit, aire2: Math.abs(cross(cotes[0], cotes[3])) };
};

const tirerQuadrilatere = () => {
    for (let essai = 0; essai < 600; essai++) {
        const v = [randInt(-5, 5), randInt(-4, 4)];
        if (Math.max(Math.abs(v[0]), Math.abs(v[1])) < 2 || egal(v, SACRIFIE)) continue;
        const M = [randInt(1, QX - 1), randInt(1, QY - 1)];
        const N = [randInt(1, QX - 1), randInt(1, QY - 1)];
        const Mp = plus(M, v);
        const Np = plus(N, v);
        if (![Mp, Np].every(dansQ)) continue;
        const d = moins(N, M);
        const lv = Math.hypot(...v);
        const ld = Math.hypot(...d);
        if (!ld || Math.abs(cross(v, d)) < 8) continue;                       // aire ≥ 8 carreaux, M, N, M' non alignés
        if (Math.max(lv, ld) / Math.min(lv, ld) < 1.3) continue;              // ni losange, ni presque
        const cos = (v[0] * d[0] + v[1] * d[1]) / (lv * ld);
        if (Math.abs(cos) < 0.25 || Math.abs(cos) > 0.92) continue;           // ni angle droit, ni quadrilatère écrasé
        const quad = [M, Mp, Np, N];
        const departs = [];
        for (let x = 1; x <= QX - 1; x++) {
            for (let y = 1; y <= QY - 1; y++) {
                const T = [x, y];
                const H = plus(T, v);
                if (!dansQ(H)) continue;
                const loin = quad.every((q, i) => distSegments(T, H, q, quad[(i + 1) % 4]) >= 1.5);
                if (loin) departs.push(T);
            }
        }
        if (!departs.length) continue;
        return { v, M, N, Mp, Np, T: randomChoice(departs) };
    }
    return null;
};

const REPLI_QUAD = { v: [3, 1], M: [1, 1], N: [3, 6], Mp: [4, 2], Np: [6, 7], T: [9, 3] };

const genNatureQuad = () => {
    const sousType = randomChoice(['nature', 'propriete']);   // mode auto, À ARBITRER
    const q = tirerQuadrilatere() || REPLI_QUAD;
    const { v, M, N, Mp, Np, T } = q;
    const nat = natureRecalculee(M, Mp, Np, N);
    if (!nat.parallelogramme || nat.losange || nat.rectangle || nat.aire2 < 8 || !egal(moins(Mp, M), v) || !egal(moins(Np, N), v)) {
        throw new Error('gen_182 nature_quad : quadrilatère');
    }
    const centre = [(M[0] + Mp[0] + Np[0] + N[0]) / 4, (M[1] + Mp[1] + Np[1] + N[1]) / 4];
    const dehors = (p) => moins(p, centre);
    const svg = svgQuadrillage({
        xMin: 0, xMax: QX, yMin: 0, yMax: QY,
        polys: [{ pts: [M, Mp, Np, N], couleur: '#475569', dash: true, opacite: 0.05 }],
        fleches: [{ a: T, b: plus(T, v), couleur: VERT }],
        points: [
            { x: M[0], y: M[1], nom: 'M', couleur: BLEU, pref: dehors(M) },
            { x: N[0], y: N[1], nom: 'N', couleur: BLEU, pref: dehors(N) },
            { x: Mp[0], y: Mp[1], nom: 'M\'', couleur: ORANGE, pref: dehors(Mp) },
            { x: Np[0], y: Np[1], nom: 'N\'', couleur: ORANGE, pref: dehors(Np) },
        ],
    });
    const intro = '<p>Sur le quadrillage, M\' et N\' sont les images des points M et N par la translation schématisée par la flèche.</p>';
    const choix = sousType === 'nature' ? CHOIX_NATURE : CHOIX_PROPRIETE;
    const question = sousType === 'nature'
        ? '<p><b>Quelle est la nature la plus précise du quadrilatère MM\'N\'N ?</b></p>'
        : '<p><b>Pourquoi le quadrilatère MM\'N\'N est-il un parallélogramme ?</b></p>';
    const payload = makeQcm(Object.values(choix), choix.cle, intro + question, {
        ...opts(L_QUAD),
        elements_additionnels: imageEnonce(svg,
            'Quadrillage : les points M et N, leurs images M\' et N\', la flèche verte de la translation tracée à part et le quadrilatère MM\'N\'N en pointillés.'),
    });
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = {
        sousType, M, N, v, depart: T,
        distracteurs: Object.keys(choix).filter((k) => k !== 'cle').map((k) => ({ code: sousType === 'nature' && k !== 'trapeze' ? 'parallelogramme_particulier' : k, choix: choix[k] })),
    };
    return payload;
};

// ═════════ Carte « Aire d'une frise » (saisie, figure) ═════════

/** Centre d'aire d'un polygone simple (formule du lacet). */
const centreAire = (pts) => {
    let a = 0; let cx = 0; let cy = 0;
    pts.forEach((p, i) => {
        const q = pts[(i + 1) % pts.length];
        const c = p[0] * q[1] - q[0] * p[1];
        a += c; cx += (p[0] + q[0]) * c; cy += (p[1] + q[1]) * c;
    });
    return [cx / (3 * a), cy / (3 * a)];
};

// Motifs à sommets entiers, aire annoncée par sa formule (vérifiée par airePoly au
// tirage) ; pas = décalage horizontal d'un motif au suivant (le chevron et le
// parallélogramme s'emboîtent, comme la frise de chevrons de l'exemple officiel).
const MOTIFS = [
    ...[[3, 1], [4, 1], [5, 1], [2, 2], [3, 2], [4, 2]].map(([a, h]) => ({
        forme: 'chevron', pts: [[0, 0], [a, 0], [a + h, h], [a, 2 * h], [0, 2 * h], [h, h]],
        aire: 2 * a * h, largeur: a + h, hauteur: 2 * h, pas: a, centre: [h + a / 2, h],
    })),
    ...[[3, 2, 1], [4, 2, 1], [3, 3, 1], [4, 3, 2], [5, 2, 2], [3, 4, 1], [5, 3, 1], [4, 4, 2], [5, 4, 1]].map(([b, h, s]) => ({
        forme: 'parallelogramme', pts: [[0, 0], [b, 0], [b + s, h], [s, h]], aire: b * h, largeur: b + s, hauteur: h, pas: b,
    })),
    ...[[4, 2, 3, 1], [5, 3, 2, 1], [6, 2, 3, 2], [6, 4, 2, 1], [5, 1, 3, 2], [6, 3, 4, 1], [5, 2, 4, 0], [4, 2, 4, 0]].map(([B, b, h, o]) => ({
        forme: 'trapeze', pts: [[0, 0], [B, 0], [o + b, h], [o, h]], aire: ((B + b) * h) / 2, largeur: B, hauteur: h, pas: B + 1,
    })),
    ...[[4, 3, 1], [4, 4, 2], [6, 3, 2], [4, 5, 3], [5, 4, 1], [6, 4, 4], [6, 5, 3], [3, 4, 3]].map(([b, h, s]) => ({
        forme: 'triangle', pts: [[0, 0], [b, 0], [s, h]], aire: (b * h) / 2, largeur: b, hauteur: h, pas: b + 1,
    })),
    ...[[2, 3, 2], [4, 2, 1], [4, 2, 2], [4, 3, 1], [4, 3, 2], [2, 4, 2], [2, 3, 1]].map(([b, h, r]) => ({
        forme: 'maison', pts: [[0, 0], [b, 0], [b, h], [b / 2, h + r], [0, h]], aire: b * h + (b * r) / 2, largeur: b, hauteur: h + r, pas: b + 1,
    })),
].map((m) => ({ ...m, centre: m.centre || centreAire(m.pts) }));

const LARGEUR_MAX = 22;

/** Point strictement intérieur à un polygone (règle de parité, points sur un bord exclus par le test de distance). */
const dedansStrict = (p, pts) => {
    if (pts.some((a, i) => distPointSegment(p, a, pts[(i + 1) % pts.length]) < 1e-9)) return false;
    let dedans = false;
    pts.forEach((a, i) => {
        const b = pts[(i + 1) % pts.length];
        if ((a[1] > p[1]) !== (b[1] > p[1]) && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) dedans = !dedans;
    });
    return dedans;
};

const listeMotifs = (n) => (n === 3 ? 'les motifs 2 et 3' : n === 4 ? 'les motifs 2, 3 et 4' : `les motifs 2 à ${n}`);

const genAireFrise = () => {
    // Le nombre de motifs d'abord (3 à 6 à parts égales), puis un motif qui tient n fois dans la largeur.
    const n = randInt(3, 6);
    const motif = randomChoice(MOTIFS.filter((m) => (n - 1) * m.pas + m.largeur <= LARGEUR_MAX));
    const question = randomChoice(['motif', 'frise']);
    const k = randInt(2, n);
    // Second chemin sur l'aire : la formule de la forme contre la formule du lacet.
    if (!Number.isInteger(motif.aire) || motif.aire < 6 || motif.aire > 20 || airePoly(motif.pts) !== motif.aire) {
        throw new Error(`gen_182 frise : aire du motif ${motif.forme}`);
    }
    const copies = Array.from({ length: n }, (_, i) => motif.pts.map(([x, y]) => [1 + i * motif.pas + x, 1 + y]));
    const largeur = (n - 1) * motif.pas + motif.largeur;
    if (largeur > LARGEUR_MAX + 1) throw new Error('gen_182 frise : trop large');
    // Les copies ne se chevauchent pas : deux copies voisines sont les copies 1 et 2 décalées, et une copie
    // ne peut toucher que ses voisines (pas ≥ moitié de la largeur) ; échantillonnage au quart de carreau.
    if (n > 1 && 2 * motif.pas < motif.largeur) throw new Error('gen_182 frise : pas trop court');
    for (let x = 1; x < 1 + motif.pas + motif.largeur; x += 0.25) {
        for (let y = 1; y < 1 + motif.hauteur; y += 0.25) {
            const e = [x + 0.125, y + 0.125];
            if (dedansStrict(e, copies[0]) && dedansStrict(e, copies[1])) throw new Error('gen_182 frise : chevauchement');
        }
    }
    if (copies.some((c) => airePoly(c) !== motif.aire)) throw new Error('gen_182 frise : copie');
    const rep = question === 'motif' ? motif.aire : n * motif.aire;
    const svg = svgQuadrillage({
        xMin: 0, xMax: largeur + 2, yMin: 0, yMax: motif.hauteur + 2,
        polys: copies.map((c, i) => ({
            pts: c, couleur: i === 0 ? BLEU : ORANGE, numero: i + 1,
            centre: [1 + i * motif.pas + motif.centre[0], 1 + motif.centre[1]],
        })),
    });
    const texte = `<p>Sur le quadrillage, ${listeMotifs(n)} sont obtenus à partir du motif 1 par des translations.</p>`
        + `<p>Le motif 1 a une aire de ${motif.aire} carreaux.</p>`
        + `<p><b>${question === 'motif' ? `Quelle est l'aire du motif ${k} ?` : 'Quelle est l\'aire de toute la frise ?'}</b></p>`;
    const payload = makeSaisie(String(rep), texte, {
        ...opts(L_FRISE), suffixe: 'carreaux',
        elements_additionnels: imageEnonce(svg, `Quadrillage : une frise de ${n} motifs numérotés de 1 à ${n}.`, 'large'),
    });
    payload._meta.params = { forme: motif.forme, aire: motif.aire, n, question, k: question === 'motif' ? k : null };
    return payload;
};

// ═════════ Carte « Angle conservé » (saisie, figure codée) ═════════

// Échelle de l'attendu (FORMATS §0.8) : le défaut de la carte, puis l'échelon marqué.
export const ECHELONS_DIFFICULTE = [
    { code: 'image_angle_marque', pose: false, geste: 'donner la mesure de l\'image de l\'angle marqué : la translation conserve les angles (défaut de la carte, sousType « direct »)' },
    { code: 'troisieme_angle', pose: true, geste: 'donner la mesure de l\'image de l\'AUTRE angle aigu : conservation des angles et somme des angles du triangle rectangle, 90 − c (sousType « troisieme_angle »)' },
];

const rad = (d) => (d * Math.PI) / 180;
const f1 = (x) => Math.round(x * 10) / 10;

/**
 * Triangle ABC rectangle en A dessiné EXACTEMENT (hypoténuse 170 px), son image
 * MNP par la translation t (pixels), la flèche de t tracée sous les deux
 * triangles, l'angle droit codé en A, l'angle aigu marqué (arc et mesure).
 */
const HYP = 170;
const svgAngle = ({ angB, marque, c, e1, e2, t }) => {
    const H = HYP;
    const A = [0, 0];
    const B = [e1[0] * H * Math.cos(rad(angB)), e1[1] * H * Math.cos(rad(angB))];
    const C = [e2[0] * H * Math.sin(rad(angB)), e2[1] * H * Math.sin(rad(angB))];
    const tri = [A, B, C];
    const img = tri.map((p) => plus(p, t));
    const tous = [...tri, ...img];
    const xs = tous.map((p) => p[0]);
    const ys = tous.map((p) => p[1]);
    const bas = Math.max(...ys);
    const gauche = Math.min(...xs);
    // flèche de la translation, sous les triangles
    const dep = [gauche + Math.max(0, -t[0]) + 10, bas + 34 + Math.max(0, -t[1])];
    const arr = plus(dep, t);
    const xsAll = [...xs, dep[0], arr[0]];
    const ysAll = [...ys, dep[1], arr[1]];
    const ox = 26 - Math.min(...xsAll);
    const oy = 26 - Math.min(...ysAll);
    const W = Math.ceil(Math.max(...xsAll) + ox + 26);
    const Ht = Math.ceil(Math.max(...ysAll) + oy + 20);
    const P = (p) => [f1(p[0] + ox), f1(p[1] + oy)];
    const pts = (arr3) => arr3.map((p) => P(p).join(',')).join(' ');
    const out = [];
    out.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${Ht}" width="${W}" height="${Ht}" font-family="system-ui, sans-serif">`);
    out.push(`<rect x="0" y="0" width="${W}" height="${Ht}" fill="white"/>`);
    out.push(`<polygon class="tri" data-nom="ABC" points="${pts(tri)}" fill="${BLEU}" fill-opacity="0.08" stroke="${BLEU}" stroke-width="2" stroke-linejoin="round"/>`);
    out.push(`<polygon class="tri" data-nom="MNP" points="${pts(img)}" fill="${ORANGE}" fill-opacity="0.08" stroke="${ORANGE}" stroke-width="2" stroke-linejoin="round"/>`);
    // angle droit en A
    const q1 = plus(A, [e1[0] * 11, e1[1] * 11]);
    const q2 = plus(q1, [e2[0] * 11, e2[1] * 11]);
    const q3 = plus(A, [e2[0] * 11, e2[1] * 11]);
    out.push(`<polyline points="${pts([q1, q2, q3])}" fill="none" stroke="${BLEU}" stroke-width="1.6"/>`);
    // angle marqué : arc de rayon 22 et mesure sur la bissectrice, à une distance où elle tient dans l'angle
    const S = marque === 'B' ? B : C;
    const autres = marque === 'B' ? [A, C] : [A, B];
    const dir = (p) => { const d = moins(p, S); const l = Math.hypot(...d); return [d[0] / l, d[1] / l]; };
    const u1 = dir(autres[0]);
    const u2 = dir(autres[1]);
    const a1 = plus(S, [u1[0] * 18, u1[1] * 18]);
    const a2 = plus(S, [u2[0] * 18, u2[1] * 18]);
    const balayage = u1[0] * u2[1] - u1[1] * u2[0] > 0 ? 1 : 0;
    out.push(`<path d="M ${P(a1).join(',')} A 18,18 0 0 ${balayage} ${P(a2).join(',')}" fill="none" stroke="${ROUGE}" stroke-width="1.6"/>`);
    const bis = [u1[0] + u2[0], u1[1] + u2[1]];
    const lb = Math.hypot(...bis);
    // Mesure sur la bissectrice : assez loin pour tenir dans l'angle (demi-ouverture ≈ 14 px), jamais
    // au-delà de 62 % du chemin jusqu'au côté opposé (sinon, pour un grand angle, elle frôle l'angle droit).
    const cote = H * Math.cos(rad(c));                          // côté de l'angle droit issu du sommet marqué
    const jusquAuCote = (2 * cote * H * Math.cos(rad(c / 2))) / (cote + H);
    const dLab = Math.min(Math.max(28, 14 / Math.sin(rad(c / 2))), 0.62 * jusquAuCote);
    const lab = plus(S, [(bis[0] / lb) * dLab, (bis[1] / lb) * dLab]);
    out.push(`<text class="mesure" x="${P(lab)[0]}" y="${f1(P(lab)[1] + 4.5)}" text-anchor="middle" font-size="13" font-weight="700" fill="${ROUGE}">${c}°</text>`);
    // noms des sommets, à l'extérieur (dans le prolongement du centre vers le sommet)
    const nommer = (triangle, noms, col) => {
        const g = [(triangle[0][0] + triangle[1][0] + triangle[2][0]) / 3, (triangle[0][1] + triangle[1][1] + triangle[2][1]) / 3];
        triangle.forEach((p, i) => {
            const d = moins(p, g); const l = Math.hypot(...d);
            const e = plus(p, [(d[0] / l) * 14, (d[1] / l) * 14]);
            out.push(`<text class="pl" x="${P(e)[0]}" y="${f1(P(e)[1] + 5)}" text-anchor="middle" font-size="15" font-weight="700" fill="${col}">${noms[i]}</text>`);
        });
    };
    nommer(tri, ['A', 'B', 'C'], BLEU);
    nommer(img, ['M', 'N', 'P'], ORANGE);
    // flèche de la translation : trait, pointe pleine de 10 px
    const L = Math.hypot(...t);
    const u = [t[0] / L, t[1] / L];
    const n = [-u[1], u[0]];
    const base = moins(arr, [u[0] * 10, u[1] * 10]);
    out.push(`<g class="fl"><line x1="${P(dep)[0]}" y1="${P(dep)[1]}" x2="${P(plus(base, u))[0]}" y2="${P(plus(base, u))[1]}" stroke="${VERT}" stroke-width="2.4" stroke-linecap="round"/>`
        + `<polygon points="${pts([arr, plus(base, [n[0] * 5, n[1] * 5]), moins(base, [n[0] * 5, n[1] * 5])])}" fill="${VERT}"/></g>`);
    out.push('</svg>');
    return out.join('');
};

const MESURES_ANGLE = Array.from({ length: 51 }, (_, i) => 20 + i).filter((x) => x !== 45);

// Orientations de l'angle droit : e1 porte [AB], e2 porte [AC] (repère écran, y vers le bas).
const ORIENTATIONS = [
    [[1, 0], [0, -1]], [[1, 0], [0, 1]], [[-1, 0], [0, -1]], [[-1, 0], [0, 1]],
    [[0, -1], [1, 0]], [[0, -1], [-1, 0]], [[0, 1], [1, 0]], [[0, 1], [-1, 0]],
];

const genAngle = () => {
    // 20 à 70, sauf 45 : l'autre angle aigu vaudrait aussi 45 et recopier l'angle donné serait juste.
    const c = randomChoice(MESURES_ANGLE);
    const marque = randomChoice(['B', 'C']);
    const sousType = randomChoice(['direct', 'troisieme_angle']);
    const [e1, e2] = randomChoice(ORIENTATIONS);
    const angB = marque === 'B' ? c : 90 - c;                   // mesure de ABC
    const angC = 90 - angB;                                      // mesure de ACB
    // Second chemin : les deux angles aigus recalculés sur le triangle dessiné.
    const B = [e1[0] * Math.cos(rad(angB)), e1[1] * Math.cos(rad(angB))];
    const C = [e2[0] * Math.sin(rad(angB)), e2[1] * Math.sin(rad(angB))];
    const angleEn = (S, X, Y) => {
        const u = moins(X, S); const w = moins(Y, S);
        return (Math.acos((u[0] * w[0] + u[1] * w[1]) / (Math.hypot(...u) * Math.hypot(...w))) * 180) / Math.PI;
    };
    if (Math.abs(angleEn(B, [0, 0], C) - angB) > 1e-6 || Math.abs(angleEn(C, [0, 0], B) - angC) > 1e-6
        || Math.abs(angleEn([0, 0], B, C) - 90) > 1e-6) throw new Error('gen_182 angle : triangle');
    // Image : à droite ou à gauche, un peu plus haut ou plus bas ; la largeur du triangle plus un écart.
    const largeur = Math.abs(e1[0]) * HYP * Math.cos(rad(angB)) + Math.abs(e2[0]) * HYP * Math.sin(rad(angB));
    const t = [randomChoice([1, -1]) * Math.round(largeur + randInt(45, 75)), randInt(-30, 30)];
    const nomMarque = marque === 'B' ? 'ABC' : 'ACB';
    const imageDe = { B: 'MNP', C: 'MPN' };                    // image de l'angle en B : en N ; en C : en P
    const autre = marque === 'B' ? 'C' : 'B';
    const demande = sousType === 'direct' ? imageDe[marque] : imageDe[autre];
    const rep = sousType === 'direct' ? c : 90 - c;
    if (rep === c && sousType !== 'direct') throw new Error('gen_182 angle : angle_recopie juste');
    const svg = svgAngle({ angB, marque, c, e1, e2, t });
    const texte = `<p>Le triangle ABC est rectangle en A et ${tex(`\\widehat{${nomMarque}}`)} = ${c}°.</p>`
        + '<p>Le triangle MNP est l\'image du triangle ABC par la translation schématisée par la flèche : M, N et P sont les images de A, B et C.</p>'
        + `<p><b>Quelle est la mesure de l'angle ${tex(`\\widehat{${demande}}`)} ?</b></p>`;
    const payload = makeSaisie(String(rep), texte, {
        ...opts(L_ANGLE), suffixe: '°',
        elements_additionnels: imageEnonce(svg,
            `Le triangle ABC rectangle en A, l'angle en ${marque} marqué ${c}°, et le triangle MNP, son image par la translation schématisée par la flèche verte.`),
    });
    payload.contenu = { ...payload.contenu, sousType, ...(sousType === 'troisieme_angle' ? { difficulte: 'troisieme_angle' } : {}) };
    payload._meta.params = { c, marque, sousType, demande, difficulte: sousType === 'troisieme_angle' ? 'troisieme_angle' : null, erreur_nommee: sousType === 'troisieme_angle' ? 'angle_recopie' : null };
    return payload;
};

// ═════════ Registre ═════════

const GENERATORS = {
    conservation_vf: genConservation,
    nature_quad_qcm: genNatureQuad,
    aire_frise_saisie: genAireFrise,
    angle_saisie: genAngle,
};

const CAT = [
    { id: 'conservation', label: L_CONSV, keys: ['conservation_vf'] },
    { id: 'quad', label: L_QUAD, keys: ['nature_quad_qcm'] },
    { id: 'frise', label: L_FRISE, keys: ['aire_frise_saisie'] },
    { id: 'angle', label: L_ANGLE, keys: ['angle_saisie'] },
];

const FMT_LABEL = { qcm: 'QCM', vf: 'Vrai/Faux', saisie: 'Saisie' };
export const CATEGORIES = CAT.map((c) => ({
    id: c.id, label: c.label,
    formatsList: c.keys.map((k) => ({ key: k, label: FMT_LABEL[k.split('_').pop()] || k })),
}));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const m = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = m.length ? randomChoice(m) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genConservation, genNatureQuad, genAireFrise, genAngle, MOTIFS, LIGNES_VF, SACRIFIE,
};
